/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AddEditFipComponent } from '../../../pages/network/floatingIps/ips/AddEditFipsComponent/add-edit-fip.component';
import { BaseComponent } from '../../../../../core/components';
import {
    ChangeDetectorRef,
    Component,
    ComponentRef,
    EventEmitter,
    Input,
    Output,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { ErrorResponseModel, ErrorsResponseModel } from '../../../../../core/models';
import { FIPApiService } from '../../http';
import { FipModel, IpGroupModel, NetworkInterfaceModel } from '../../models';
import { FloatingIpsService, NetworkingService, WizardService } from '../../services/components';
import {
    LanguageService,
    LoadingService,
    LogService,
    ModalService,
    NotifyService,
    RbacService,
    TransactionService,
    UtilsService
} from '../../../../../core/services';
import { TableMultiSelectModel } from '../../../../../core/models/tableMultiSelect.model';
import { catchError, concatMap, mergeMap, takeUntil } from 'rxjs/operators';
import { from, Observable, of, Subscriber } from 'rxjs';

@Component({
    selector: 'app-lb-floating-ip',
    templateUrl: './floatingIP.component.html',
    styleUrls: ['./floatingIP.component.scss']
})

export class FloatingIPComponent extends BaseComponent {
    /**
     * Add / Edit container.
     */
    @ViewChild('addEditContainer', { read: ViewContainerRef, static: true })
    public addEditContainer: any;

    /**
     * Determines the way we style the component e.g. in and out of the wizard.
     */
    @Input() public componentEmbedded: boolean = false;

    /**
     * Inputs used when not routing to this page (e.g. from a wizard).
     * @param fips
     */
    @Input() set fips(fips: FipModel[]) {
        this._fips = fips;
        this.inputHelper();
    }

    /**
     * Inputs used when not routing to this page (e.g. from a wizard).
     * @param ipGroups
     */
    @Input() set groups(ipGroups: any[]) {
        this._groups = ipGroups;
        this.inputHelper();
    }

    /**
     * Input used for external validation from another component.
     * @param validation
     */
    @Input() set validationError(validation: ErrorResponseModel) {
        this._externalValidationError = validation;
        if (this._externalValidationError?.message) {
            const fips: AbstractControl = this.ipForm.get('fips');
            fips.markAsTouched();
            fips.setErrors({ 'responseError': this._externalValidationError.message });
        }
    }

    /**
     * Incoming IP ID for pre-selection in table.
     * @param key
     */
    @Input() set selectedKey(key: string) {
        if (key) {
            this._setSelectedKey = key;
            this.selectedRowObject = this.tableRows.find(row => row.id === this._setSelectedKey);
        }
    }

    /**
     * Selected IP ID.
     */
    @Output() public fipId: EventEmitter<string> = new EventEmitter<string>();

    /**
     * Selected IP ID.
     */
    @Output() public fip: EventEmitter<string> = new EventEmitter<string>();

    /**
     * Floating IP group objects.
     */
    public _groups: IpGroupModel[] = [];

    /**
     * Network interface objects.
     */
    public _fips: FipModel[] = [];

    /**
     * Interfaces
     */
    public interfaces: NetworkInterfaceModel[] = [];

    /**
     * Table row data.
     */
    public tableRows: any[] = [];

    /**
     * Temporary list of table objects used for searching (Table ONLY).
     */
    public tempRows: any[] = [];

    /**
     * Store selected row object for table state checks.
     */
    public selectedRowObject: any = [];

    /**
     * To store the actions and text for the tables select all functions.
     */
    public actionDotsArray: any = [];

    /**
     * New IP FormGroup.
     */
    public ipForm: FormGroup;

    /**
     * Flag to determine if in add/edit mode.
     */
    public addEditMode: boolean = false;

    /**
     * Store the selectAll data.
     */
    public multiSelectActions: TableMultiSelectModel[] = [];

    /**
     * To store the progress message.
     */
    public progressMessage: string = '';

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * Store selected row key.
     * @private
     */
    private _setSelectedKey: string;

    /**
     * Modal Component Reference.
     * @private
     */
    private componentReference: ComponentRef<any>;

    /**
     * Store any external validation errors.
     * @private
     */
    private _externalValidationError: ErrorResponseModel;

    constructor(
        public modalService: ModalService,
        private cdr: ChangeDetectorRef,
        private fipService: FIPApiService,
        private floatingIpsService: FloatingIpsService,
        private formBuilder: FormBuilder,
        private languageService: LanguageService,
        private loadingService: LoadingService,
        private logService: LogService,
        private networkingService: NetworkingService,
        private notifyService: NotifyService,
        private rbacService: RbacService,
        private route: ActivatedRoute,
        private router: Router,
        private transactionService: TransactionService,
        private utilsService: UtilsService,
        private wizardService: WizardService
    ) {
        super();

        this.setupFormGroup();
        this.setUpPageData();
        this.registerButtons();
        this.setSelectAll();
        this.selectAllActionDotButtons();
    }

    /**
     * Get the selected key.
     */
    get selectedKey() {
        return this._setSelectedKey;
    }

    /**
     * Get a list of fips
     */
    get fips() {
        return this._fips;
    }

    /**
     * Get a list of network groups
     */
    get groups() {
        return this._groups;
    }

    /**
     * Check if there is external validation and if it applies to this fip id.
     * @param fipId
     */
    public checkFipIdValidation(fipId: string): boolean {
        const validationValue: string = this._externalValidationError?.value;

        return validationValue === fipId && validationValue === this._setSelectedKey;
    }

    /**
     * Form function triggered when a table row is selected.
     * @param event
     */
    public onSelect(event: any): void {
        if (event[event?.length - 1]?.id) {
            const newFipId = this.utilsService.getObjectValue(event[event.length - 1], 'id');
            const currentFipId = this.utilsService.getObjectValue(this.selectedRowObject[event.length - 2], 'id');
            if (newFipId !== currentFipId) {
                this.selectedRowObject = event;
                this.fipId.emit(event[0].id);
                this.fip.emit(event[0]);
            } else {
                this.fipId.emit(this.selectedRowObject.id);
                this.fip.emit(this.selectedRowObject);
            }
            this.setIpValidator(newFipId);
            this.multiSelectActions[0].buttonAction = this.confirmEnableDisableMultipleIPs.bind(this, this.selectedRowObject, 'enable');
            this.multiSelectActions[1].buttonAction = this.confirmEnableDisableMultipleIPs.bind(this, this.selectedRowObject, 'disable');
            this.multiSelectActions[2].buttonAction = this.confirmDeleteMultipleIPs.bind(this, this.selectedRowObject);
        } else if (typeof event === 'object') {
            this.selectedRowObject = event;
            this.fipId.emit(event.id);
            this.fip.emit(event);
        } else {
            this.fipId.emit(null);
            this.fip.emit(null);
        }
    }

    /**
     * Confirm enable/disable fip
     * @param row
     * @param state
     */
    public confirmEnableDisableFip(row: FipModel, state: string): void {
        const confirmationObject = {
            title: 'floating_ip_' + state,
            message: 'Are you sure you would like to ' + state + ' IP ' + row.ip + '?',
            callBack: this.enableDisableFips.bind(this, state, row),
            options: { translateText: false }
        };
        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Confirm enable/disable all fips
     * @param state
     */
    public confirmEnableDisableAllFips(state: string): void {
        const confirmationObject = {
            title: 'floating_ip_all_' + state,
            message: 'Are you sure you would like to ' + state + ' all IPs?',
            callBack: this.enableDisableFips.bind(this, state),
            options: { translateText: false }
        };
        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Confirm delete fip
     * @param row
     */
    public confirmDeleteFip(row: FipModel): void {
        const confirmationObject = {
            title: 'delete_ip',
            message: 'Are you sure you would like to delete IP ' + row.ip + '?',
            callBack: this.deleteIP.bind(this, row.id),
            options: { translateText: false }
        };
        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Add or edit IP.
     * @param row
     */
    public addEditIP(row?: FipModel): void {
        this.addEditMode = true;
        this.wizardService.disableNavigation = true;

        if (this.componentEmbedded) {
            this.setUpComponentFactory(row);
        } else {
            const url: string = row ? 'edit/' + row.id : 'add/';
            this.router.navigate([url], { relativeTo: this.route }).then().catch();
        }
    }

    /**
     * Enable/disable single fip.
     * @param row
     * @param state
     * @private
     */
    private enableDisableFips(state: string, row?: FipModel): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.getFipApiCall(state, (row?.id ? [row?.id] : []))
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.setUpPageData();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                })
                .add((): void => {
                    this.loadingService.loadingActionDotsEmitter.next(false);
                });
        });
    }

    /**
     * Returns formatted string for displaying in the table.
     * @param label
     * @param network
     * @param mask
     * @private
     */
    private static formatNetworkData(label: string, network: any, mask: any): string {
        return network && mask ? `${label} ${network}/${mask}` : '';
    }

    /**
     * Set up the component factory.
     * @param row
     * @private
     */
    private setUpComponentFactory(row?: FipModel): void {
        const addEditComponent = this.addEditContainer.createComponent(AddEditFipComponent);
        addEditComponent.instance.componentEmbedded = this.componentEmbedded;
        addEditComponent.instance.editId = row ? row.id : '';
        addEditComponent.instance.callBack = this.addNewIpHelper.bind(this);

        this.componentReference = addEditComponent;
        this.cdr.detectChanges();
    }

    /**
     * Sets the floating IP data from the API response.
     * @param fipId
     * @private
     */
    private setUpPageData(fipId?: string): void {
        this.floatingIpsService.getFloatingIpData()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    this.fips = data['ips'];
                    this.interfaces = data['interfaces'];
                    this.groups = data['groups'];
                    this.populateRows();
                    if (fipId) {
                        this.selectedRowObject = this.filterTableRows(fipId);
                        this.fipId.emit(this.selectedRowObject[0].id);
                        this.fip.emit(this.selectedRowObject[0]);
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('FloatingIPComponent:setUpPageData error', error.message);
                }
            });
    }

    /**
     * Populate table rows for displaying in the table.
     * @private
     */
    private populateRows(): void {
        this.tableRows = [];

        for (const ip of this.fips) {
            const group = this.groups.find(item => item.id === ip.groupId);
            const assignedInterface = this.networkingService.getLocalAssignedInterface(group, this.interfaces);
            const usage: any[] = this.updateUsage(ip.usage);

            this.tableRows.push({
                enabled: ip?.enabled,
                fipStatus: ip?.enabled ? 'enabled' : 'disabled',
                groupName: `${group.name} (${assignedInterface?.settings?.interfaceName})`,
                id: ip.id,
                ip: ip.ip,
                ipv4: FloatingIPComponent.formatNetworkData('IPv4', group.ipv4Network, group.ipv4Mask),
                ipv6: FloatingIPComponent.formatNetworkData('IPv6', group.ipv6Network, group.ipv6Mask),
                usage,
                usageText: this.formatUsageText(usage)
            });
            this.tempRows = [...this.tableRows];
        }

        this.selectedRowObject = this.tableRows.filter(row => row.id === this.selectedKey);
        // Wait for the dom updated and then scroll to the selected IP.
        this.utilsService.scrollToElement('.selected-row');
        if (this.tableRows.length === 1 && this.componentEmbedded) {
            const fip = this.tableRows[0];
            this.selectedKey = fip.id;
            this.fipId.emit(this.selectedKey);
            this.fip.emit(fip);
        }
    }

    /**
     * Format usage text so that its searchable.
     * @param usage
     * @private
     */
    private formatUsageText(usage: any[]): string {
        return usage.length ? `Used in ${usage.length} place/s` : 'Not in use';
    }

    /**
     * Set the select all button.
     */
    public setSelectAll(): void {
        this.multiSelectActions = [
            {
                buttonText: 'floating_ip_enable_selected',
                buttonAction: this.confirmEnableDisableMultipleIPs.bind(this),
                buttonDisabled: !this.permissionsLookup['floating-ip-enable']
            },
            {
                buttonText: 'floating_ip_disable_selected',
                buttonAction: this.confirmEnableDisableMultipleIPs.bind(this, this.selectedRowObject, 'disable'),
                buttonDisabled: !this.permissionsLookup['floating-ip-disable']
            },
            {
                buttonText: 'floating_ip_delete_multiple_ips',
                buttonAction: this.confirmDeleteMultipleIPs.bind(this),
                buttonDisabled: !this.permissionsLookup['floating-ip-delete']
            },
        ];
    }

    /**
     * Update the usage object in each array item
     * @param usage
     * @private
     */
    private updateUsage(usage: any[]): any[] {
        return usage.map((item: any) => {
            const serviceType = item.service === 'haproxy' ? 'layer7' : 'layer4';
            item.link = `/cluster-configuration/manage-services/service-details/${serviceType}/${item.serviceId}`;

            return item;
        });
    }

    /**
     * Callback used once the add/edit component has been closed in the service.
     * @param fipId
     * @private
     */
    private addNewIpHelper(fipId: string): void {
        this.addEditMode = false;
        this.wizardService.disableNavigation = false;
        this.componentReference.destroy();
        this.setUpPageData(fipId);
        this.selectedRowObject = this.filterTableRows(fipId);
    }

    /**
     * Filter the table rows to find the matching FIP ID.
     * @param fipId
     * @private
     */
    private filterTableRows(fipId: string): any {
        return this.tableRows.filter(row => row.id === fipId);
    }

    /**
     * Use with @Input, when both values are set call populate rows.
     * @private
     */
    private inputHelper(): void {
        if ((this.groups?.length) && (this.fips?.length)) {
            this.populateRows();
        }
    }

    /**
     * Set the select all action dots object to store the button text and action.
     * @private
     */
    private selectAllActionDotButtons(): void {
        this.actionDotsArray = [
            { buttonText: 'floating_ip_delete_selected' },
            { buttonText: 'floating_ip_enable_selected' },
            { buttonText: 'floating_ip_disable_selected' }
        ];
    }

    /**
     * Confirm deletion of multiple IPs.
     * @param selectedRows
     */
    public confirmDeleteMultipleIPs(selectedRows: FipModel[]): void {
        const message: string = selectedRows.length > 1
            ? `Are you sure you want to delete ${selectedRows.length} floating IPs?`
            : `Are you sure you want to delete floating IP '${selectedRows[0].ip}' ?`;
        const confirmationObject = {
            title: 'floating_ip_delete_multiple_ips',
            message: message,
            callBack: this.deleteMultipleIPsTransaction.bind(this, selectedRows),
            options: { translateText: false }
        };
        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Confirm deletion of multiple IPs.
     * @param selectedRows
     * @param state
     */
    public confirmEnableDisableMultipleIPs(selectedRows: FipModel[], state: string): void {
        const message: string = selectedRows.length > 1
            ? `Are you sure you want to ${state} ${selectedRows.length} floating IPs?`
            : `Are you sure you want to ${state} floating IP '${selectedRows[0].ip}' ?`;
        const confirmationObject = {
            title: `floating_ip_${state}_selected`,
            message: message,
            callBack: this.enableDisableSelectedFips.bind(this, selectedRows, state),
            options: { translateText: false }
        };
        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Enable/disable selected floating IPs.
     * @param selectedRows
     * @param state
     * @private
     */
    private enableDisableSelectedFips(selectedRows: FipModel[], state: string): Observable<any> {
        const filterState: FipModel[] = selectedRows.filter((row: FipModel): boolean => row.enabled === (state !== 'enable'));
        const fipIds: string[] = filterState.map((filterState: FipModel) => filterState.id);

        return new Observable((observer: Subscriber<any>): void => {
            this.getFipApiCall(state, fipIds)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.setUpPageData();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('FloatingIPComponent:enableDisableSelectedFips() error', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                })
                .add((): void => {
                    this.loadingService.loadingActionDotsEmitter.next(false);
                });
        });
    }

    /**
     * Get api call for fips enable/disable action.
     * @param state
     * @param fipIds
     * @private
     */
    private getFipApiCall(state: string, fipIds?: any[]): any {
        return (state !== 'enable')
            ? this.fipService.disableFloatingIPs({ 'Ids': fipIds })
            : this.fipService.enableFloatingIPs({ 'Ids': fipIds });
    }

    /**
     * Delete multiple Ips transaction.
     * @param selectedRows
     * @private
     */
    private deleteMultipleIPsTransaction(selectedRows: FipModel[]): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.notifyService.muteLocalNotifications();
            this.notifyService.removeNotificationById('errored-delete-fips');

            this.transactionService.startTransaction()
                .pipe(
                    concatMap(() => this.deleteMultipleIPs(selectedRows)),
                    concatMap((erroredIps) => this.commitTransaction(erroredIps)),
                    takeUntil(this.destroyed$)
                )
                .subscribe({
                    next: (erroredIps: string[]): void => {
                        if (erroredIps.length) {
                            this.notifyService.unmuteLocalNotifications();
                            const ipString = this.utilsService.replaceLastCommaWithAnd(erroredIps.toString());
                            const errorMessage: string = this.languageService.translate('cant_be_deleted');
                            this.notifyService.error(`${ipString}  ${errorMessage}`, { id: 'errored-delete-fips' });
                        }
                        this.setUpPageData();
                        this.modalService.close('fipProgressModal');
                        this.notifyService.unmuteLocalNotifications();
                        this.notifyService.success('floating_ip_delete_message', { messageTranslate: true });
                        this.progressMessage = '';
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error(
                            'ManageServices:deleteMultipleFrontendsTransaction',
                            'Error with the transaction',
                            error
                        );
                        this.transactionService.rollbackTransaction();
                        observer.error(error);
                    }
                });
        });
    }

    /**
     * Delete multiple IPs.
     * @param selectedRows
     * @private
     */
    private deleteMultipleIPs(selectedRows: FipModel[]): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const translation: string = this.languageService.translate('floating_ip_deleting_multiple_ip');
            const ofText: string = this.languageService.translate('of');
            let index: number = 1;
            if (selectedRows.length > 1) {
                this.modalService.openModalInParent('fipProgressModal');
                this.progressMessage = `${translation} ${index} ${ofText} ${selectedRows.length}`;
            }

            const erroredIps: string[] = [];
            const dataToSubmit: Observable<FipModel> = from(selectedRows || []);
            const call: Observable<any> = dataToSubmit.pipe(
                // TODO: replace with new api endpoint once implemented.
                // TODO: This will have the same behaviour as enable/disable fips (array of fip ids passed)
                // Process each item in data to submit and catch any errors on the call.
                // Catching the error this way lets the function continue even if one call fails/errors.
                // The caught error is passed to the success response for processing (success is called for each item in the data).
                // complete is only called once and therefore that's where setUpPageData() is called.
                mergeMap((row: FipModel) => this.fipService.deleteFIP(row.id)
                    .pipe(catchError(error => of(error)))
                ),
                takeUntil(this.destroyed$)
            );
            call.subscribe({
                next: (response: any): void => {
                    if (response.status === 'fail') {
                        erroredIps.push(this.updateNotification(response.error.errors));
                    }

                    if (selectedRows.length > 1) {
                        this.progressMessage = `${translation} ${index} ${ofText} ${selectedRows.length}`;
                    }
                    index++;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('FloatingIPComponent:deleteMultipleIPs() error', error.message);
                    observer.error(error);
                },
                complete: (): void => {
                    observer.next(erroredIps);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Commit transaction.
     * @private
     */
    private commitTransaction(errorState: string[]): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.transactionService.commitTransaction()
                .subscribe({
                    next: (): void => {
                        observer.next(errorState);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Helper function to update the current notification with more details. e.g. ip address.
     * @param errors
     * @private
     */
    private updateNotification(errors: ErrorsResponseModel): string {
        const message = errors[0]?.message;
        const value = errors[0]?.value;
        if (message && value) {
            const fip: FipModel = this.fips.find((item: FipModel): boolean => item.id === value);

            return fip.ip;
        }
    }

    /**
     * Delete floating IP.
     * @param ipId
     * @private
     */
    private deleteIP(ipId: string): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.fipService.deleteFIP(ipId)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.setUpPageData();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('FloatingIPComponent:deleteIP() error', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                })
                .add((): void => {
                    this.loadingService.loadingActionDotsEmitter.next(false);
                });
        });
    }

    /**
     * Setup Floating IP form for external validation e.g. from the wizard.
     * @private
     */
    private setupFormGroup(): void {
        this.ipForm = this.formBuilder.group({
            fips: [null]
        });
    }

    /**
     * Set the validator on the form based on the ip and external validation.
     * @param ipId
     * @private
     */
    private setIpValidator(ipId?: string): void {
        const fips: AbstractControl = this.ipForm.get('fips');
        if (this._externalValidationError?.value === ipId) {
            fips.markAsTouched();
            fips.setErrors({ 'responseError': this._externalValidationError.message });
        } else {
            if (fips.hasError('responseError')) {
                fips.setErrors({ 'responseError': null });
            }
            fips.markAsUntouched();
            fips.markAsPristine();
            fips.updateValueAndValidity();
        }
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'floating-ip-add': [{ path: '/fips', methods: ['POST'] }],
            'floating-ip-edit': [{ path: '/fips/:id', methods: ['PUT'] }],
            'floating-ip-delete': [{ path: '/fips/:id', methods: ['DELETE'] }],
            'floating-ip-enable': [{ path: '/fips/enable', methods: ['PUT'] }],
            'floating-ip-disable': [{ path: '/fips/disable', methods: ['PUT'] }],

        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
